import React, { useState } from "react";
import { Modal } from "@components/modals";
import { SecretManagerHandler, SecretManager, CreateSecretManagerPayload, UpdateSecretManagerPayload } from "@api/secret-manager-handler";
import { errorAlert, successAlert } from "@components/toastify/notify-toast";
import { useSelector } from "react-redux";
import { RootState } from "@store/types";
import styles from "./../styles.module.scss";
import classNames from "classnames";
import Form, { InputField, NewSelectField } from "@components/form";
import * as yup from "yup";
import { InPageSpinner } from "@components/spinners/in-page-spinner";
import { awsRegions } from "@pages/file-browser/ui/StorageConnectionForm";

interface SecretManagerModalProps {
    showModal: boolean;
    toggleClose: () => void;
    onSuccess: () => void;
    secretData: SecretManager | null;
}

const secretManagerSchema = yup.object({
    name: yup.string().required("Name is required"),
    description: yup.string(),
    roleArn: yup.string().required("Role ARN is required"),
    externalId: yup.string().required("External ID is required"),
    region: yup.string().required("Region is required"),
    secretName: yup.string().required("Secret name is required"),
});

type SecretManagerFormValues = yup.InferType<typeof secretManagerSchema>;

const SecretManagerModal: React.FC<SecretManagerModalProps> = ({
    showModal,
    toggleClose,
    onSuccess,
    secretData,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);

    const isEditMode = !!secretData;

    const initialValues: SecretManagerFormValues = {
        name: secretData?.name || "",
        description: secretData?.description || "",
        roleArn: secretData?.roleArn || "",
        externalId: secretData?.externalId || "",
        region: secretData?.region || "",
        secretName: secretData?.secretName || "",
    };

    const handleFormSubmit = (values: SecretManagerFormValues) => {
        setIsSubmitting(true);

        if (isEditMode && secretData) {
            const payload: UpdateSecretManagerPayload = {
                id: secretData.id,
                name: values.name.trim(),
                description: values.description?.trim() || "",
                roleArn: values.roleArn.trim(),
                externalId: values.externalId.trim(),
                region: values.region.trim(),
                secretName: values.secretName.trim(),
            };

            SecretManagerHandler.UpdateSecretManager(
                `${Env?.REACT_APP_PLATFORM_URL}/vault/v1`,
                secretData.id,
                payload,
                () => {
                    successAlert("Secret updated successfully");
                    setIsSubmitting(false);
                    onSuccess();
                },
                (error) => {
                    errorAlert(error?.data?.message || "Failed to update secret");
                    setIsSubmitting(false);
                }
            );
        } else {
            const payload: CreateSecretManagerPayload = {
                name: values.name.trim(),
                description: values.description?.trim() || "",
                roleArn: values.roleArn.trim(),
                externalId: values.externalId.trim(),
                region: values.region.trim(),
                secretName: values.secretName.trim(),
            };

            SecretManagerHandler.CreateSecretManager(
                `${Env?.REACT_APP_PLATFORM_URL}/vault/v1`,
                payload,
                () => {
                    successAlert("Secret created successfully");
                    setIsSubmitting(false);
                    onSuccess();
                },
                (error) => {
                    errorAlert(error?.data?.message || "Failed to create secret");
                    setIsSubmitting(false);
                }
            );
        }
    };

    return (
        <Modal
            title={isEditMode ? "Edit Secret" : "Add New Secret"}
            isOpen={showModal}
            toggleClose={toggleClose}
            className={classNames(
                'editConnection_container',
                styles['addEditNifiModal']
            )}
            showCloseMark={true}
        >
            <div className={styles["addEditNifiModal__innerContainer"]}>
                <Form
                    initialValues={initialValues}
                    validationSchema={secretManagerSchema}
                    onSubmit={handleFormSubmit}
                    enableReinitialize
                >
                    {({ _formikprops }) => (
                        <>
                            <div className={styles["userInfoForm"]}>
                                <InputField
                                    name="name"
                                    label="Name"
                                    required
                                    placeholder="Enter secret name"
                                    className="mb-0"
                                    infoText="A unique identifier for this secret manager configuration."
                                />
                                <InputField
                                    name="description"
                                    label="Description"
                                    placeholder="Enter description (optional)"
                                    className="mb-0"
                                    infoText="Optional description to help identify the purpose of this secret manager."
                                />
                                <InputField
                                    name="roleArn"
                                    label="Role ARN"
                                    required
                                    placeholder="arn:aws:iam::123456789012:role/SecretsAccessRole"
                                    className="mb-0"
                                    infoText="The Amazon Resource Name (ARN) of the IAM role that grants access to AWS Secrets Manager."
                                />
                                <InputField
                                    name="externalId"
                                    label="External ID"
                                    required
                                    placeholder="Enter external ID"
                                    className="mb-0"
                                    infoText="A unique identifier used for cross-account access. This adds an extra layer of security when assuming the IAM role."
                                />
                                <NewSelectField
                                    name="region"
                                    label="Region"
                                    options={awsRegions}
                                    required={true}
                                    className="mb-0"
                                    infoText="The AWS region where your secrets are stored in AWS Secrets Manager."
                                />
                                <InputField
                                    name="secretName"
                                    label="Secret name"
                                    required
                                    placeholder="Enter secret name"
                                    className="mb-1"
                                    infoText="A name to identify the session when assuming the IAM role. Useful for auditing and tracking API calls."
                                />
                            </div>
                            <div className={styles["addEditNifiModal__actionBtns"]}>
                                <button
                                    className="btn btn-lg btn-yellow"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <InPageSpinner size="XSmall" color="black" className={styles['auto']} />
                                    ) : (
                                        isEditMode ? "Update" : "Create"
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

export default SecretManagerModal;
